/**
 * CSV cell encoding.
 *
 * Two separate jobs, and only one of them is about CSV syntax:
 *
 *   1. **Quoting** — a cell containing a comma, quote or newline has to be
 *      wrapped, or it silently becomes several cells.
 *   2. **Formula neutralisation** — a cell that STARTS with `=`, `+`, `-` or
 *      `@` is executed as a formula when the file is opened in Excel, Sheets or
 *      Numbers. That is CSV injection, and the values flowing through here are
 *      operator- and customer-supplied: package names, recipient names,
 *      references. A payout recipient called `=HYPERLINK(...)` lands in a file
 *      that gets opened by a human and, in the DuitNow case, uploaded to a bank.
 *
 * The second was missing until 2026-08-21.
 */

/** Characters a spreadsheet treats as the start of a formula. */
const FORMULA_START = /^[=+\-@\t\r]/;

/**
 * A plain number, which must NOT be neutralised.
 *
 * This is the whole subtlety: `-100` starts with a formula character and is
 * also a perfectly ordinary negative amount. Prefixing it would turn a number
 * into text and quietly corrupt every negative figure in an accounting export —
 * a worse outcome than the injection it was defending against.
 */
const PLAIN_NUMBER = /^-?\d+(?:\.\d+)?$/;

/**
 * Encode one value as a CSV cell: neutralise formulas, then quote if needed.
 *
 * Neutralisation prefixes a single quote, the convention every major spreadsheet
 * understands as "treat the rest as text". It is applied before quoting so the
 * prefix is inside the quotes where it belongs.
 */
export function csvCell(raw: string): string {
  if (raw === '') return '';

  const value = FORMULA_START.test(raw) && !PLAIN_NUMBER.test(raw) ? `'${raw}` : raw;

  const needsQuote = /[",\r\n]/.test(value);
  if (!needsQuote) return value;
  return `"${value.replace(/"/g, '""')}"`;
}
